import { useContext, useMemo } from 'react';
import AppliedFiltersContext from '~/contexts/AppliedFiltersContext';
import FacetDataContext from '~/contexts/FacetDataContext';
import type {
  AppliedFilters,
  FacetData,
  FacetLists,
  FacetRanges,
  MakeType
} from '~/contracts';
import type {
  AvailabilityType,
  ModelType,
  RangeFacet
} from '~/contracts/AppliedFilters';
import { isDefined } from '~/utilities/array';
import { logError } from '~/utilities/errorHandlers';
import {
  addDaysToDate,
  formatDate,
  weekdays
} from '~/utilities/formatAppliedTag';
import {
  booleanFacets,
  facetsWithDisplayName,
  rangeFacets,
  Tag
} from './constants';

const getSingleRange = (value: RangeFacet) => {
  const keys = Object.keys(value);
  if (keys.length !== 1) return;

  return { key: keys[0], value: value.min ?? value.max };
};

// we use the duplicate trims to decide if we need to show "<Model> <Trim>" or just "<Trim>" for trim tags
const getDuplicateTrims = (makes: MakeType[]): string[] => {
  const allTrims =
    makes.flatMap(make => make?.models?.flatMap(model => model?.trims)) ?? [];

  return allTrims
    .filter(
      (trim, index, array) =>
        array.indexOf(trim) !== index && array.lastIndexOf(trim) === index
    )
    .filter(isDefined);
};

const getAvailabilityTag = (
  facetKey: 'earliestDayToDeliveryOrPickup',
  values: AvailabilityType
): Tag => {
  const day = addDaysToDate(values.days);
  const date = formatDate(day);

  const tag: Tag = {
    context: { key: facetKey },
    facetKey,
    tagName: ''
  };

  const getItTomorrow = values.days === 1;
  if (getItTomorrow) tag.tagName = 'Get It By Tomorrow';

  const getItByDay = values.days > 1 && values.days < 7;
  if (getItByDay) tag.tagName = `Get it by ${weekdays[day.getDay()]}`;

  const getItByDate = values.days > 7;
  if (getItByDate) tag.tagName = `Get it by ${date}`;

  return tag;
};

const getMakeModelTrimTags = (facetKey: 'makes', values: MakeType[]): Tag[] => {
  const duplicateTrims = getDuplicateTrims(values);

  return values?.flatMap(make => {
    const makeData = {
      context: {
        key: make.name
      },
      facetKey,
      tagName: make.name
    };

    const modelData =
      make.models?.map(model => ({
        context: {
          key: model.name,
          parents: [make.name] // updateMakes expects [make] for model's parents
        },
        facetKey,
        tagName: model.name
      })) ?? [];

    const trimData =
      make.models?.flatMap(
        (model: ModelType) =>
          model?.trims?.map(trim => ({
            context: {
              key: trim,
              parents: [make.name, model.name] // updateMakes expects [make, model] for trims parents
            },
            facetKey,
            tagName: duplicateTrims.includes(trim)
              ? `${model.name} ${trim}`
              : trim
          })) ?? []
      ) ?? [];

    return [makeData, ...modelData, ...trimData];
  });
};

const getRangeFilterTag = (
  facetKey: keyof FacetRanges | 'finance',
  value: RangeFacet & { downPayment: number; monthlyPayment: number }
): Tag => {
  // return the towingCapacity filter with '## - ## Towing Capacity' or '## Towing Capacity' format
  if (facetKey === 'towingCapacity') {
    return {
      context: { key: facetKey },
      facetKey,
      tagName:
        value.min === value.max
          ? `${value.min?.toLocaleString()} Towing Capacity`
          : `${value.min?.toLocaleString()} - ${value.max?.toLocaleString()} Towing Capacity`
    };
  }

  // return the seatingCapacity filter with '## - ## Seats' or '## Seats' format
  if (facetKey === 'seatingCapacity') {
    return {
      context: { key: facetKey },
      facetKey,
      tagName:
        value.min === value.max
          ? `${String(value.min)} Seats`
          : `${value.min} - ${value.max} Seats`
    };
  }

  if (facetKey === 'year') {
    return {
      context: { key: facetKey },
      facetKey,
      tagName:
        value.min === value.max
          ? String(value.min)
          : `${value.min} - ${value.max}`
    };
  }

  // return price filter with '$## - $##' or '$##' or 'Under $##'
  if (facetKey === 'price') {
    const singleValue = getSingleRange(value);

    const tag =
      value.min === value.max
        ? `$${value.min?.toLocaleString()}`
        : `$${value.min?.toLocaleString()} - $${value.max?.toLocaleString()}`;

    return {
      context: { key: facetKey },
      facetKey,
      tagName: singleValue
        ? `${
            singleValue.key === 'min' ? 'Over' : 'Under'
          } $${singleValue.value?.toLocaleString()}`
        : tag
    };
  }

  // return the finance filter or filters with '## - ## MPG' format
  if (facetKey === 'finance') {
    return {
      context: { key: facetKey },
      facetKey,
      tagName: `$${value.downPayment.toLocaleString()} Down & $${value.monthlyPayment.toLocaleString()}/mo.`
    };
  }

  // handle other range filters
  const singleValue = getSingleRange(value);
  const tag =
    value.min === value.max
      ? value.min?.toLocaleString()
      : `${value.min?.toLocaleString()} - ${value.max?.toLocaleString()}`;
  const singleValueTag = `${
    singleValue?.key === 'min' ? 'Over' : 'Under'
  } ${singleValue?.value?.toLocaleString()}`;

  return {
    context: { key: facetKey },
    facetKey,
    tagName: `${singleValue ? singleValueTag : tag} ${
      facetKey.charAt(0).toUpperCase() + facetKey.slice(1)
    }`
  };
};

const getDisplayNameFromFacetData = (
  facetData: Partial<FacetData>,
  facetKey: keyof FacetLists | 'isFreeDelivery' | 'tagIds',
  value: any
): Tag[] => {
  if (facetKey === 'isFreeDelivery') {
    return value
      ? [
          {
            context: { key: facetKey },
            facetKey,
            tagName: facetData?.waysToSave?.isFreeDelivery?.displayName ?? ''
          }
        ]
      : [];
  }

  if (!Array.isArray(value)) {
    return [];
  }

  return (
    facetData[facetKey]?.buckets
      ?.filter(v => value.includes(v.key))
      ?.map<Tag>(v => ({
        context: { key: v.key },
        tagName: v.displayName,
        facetKey
      })) ?? []
  );
};

/**
 * We use this to get the filter tags for each applied filter.
 * To do this we loop through facet data and for each applied filter
 * we either return the displayName or build a tag for it.
 * The only tags we need to build still are Range tags.
 */
const useGetFilterTags = (
  shouldGenerate: boolean = true,
  savedFilters?: AppliedFilters
): Tag[] => {
  const { facetData } = useContext(FacetDataContext);
  const { appliedFilters } = useContext(AppliedFiltersContext);

  const filtersToTag = savedFilters ?? appliedFilters;

  return useMemo<Tag[]>(() => {
    if (!shouldGenerate) return [];

    const getTags = (tags: Tag[], [facetKey, value]: [any, any]): Tag[] => {
      try {
        const isEmpty =
          Object.keys(value).length === 0 && !booleanFacets.includes(facetKey);
        if (isEmpty) return tags;

        if (facetsWithDisplayName.includes(facetKey)) {
          const tagsFromFacetData = getDisplayNameFromFacetData(
            facetData,
            facetKey,
            value
          );

          return [...tags, ...(tagsFromFacetData ?? [])];
        }

        if (facetKey === 'earliestDayToDeliveryOrPickup') {
          const availabilityTag = getAvailabilityTag(facetKey, value);

          return [...tags, availabilityTag];
        }

        if (rangeFacets.includes(facetKey)) {
          const rangeTag = getRangeFilterTag(facetKey, value);

          return [...tags, rangeTag];
        }

        if (facetKey === 'makes') {
          const makesTags = getMakeModelTrimTags(facetKey, value) ?? [];

          return [...tags, ...makesTags];
        }

        return tags;
      } catch (e) {
        logError(new Error('Failed to generate filter tags'), {
          location: 'useGetFilterTags',
          facetKey,
          value
        });

        return [];
      }
    };

    const filterTags = Object.entries(filtersToTag).reduce(getTags, []);

    return filterTags ?? [];
  }, [filtersToTag, facetData, shouldGenerate]);
};

export default useGetFilterTags;
